
import { defineComponent, PropType, computed } from 'vue';
import { PreviewEvent, PreviewOutcome } from '../../../../../types/sport';
import { GenericOption } from '../../../../../types/components';
import OddsInfo from './OddsInfo.vue';
import OddsOutcomeColumn from './OddsOutcomeColumn.vue';

export default defineComponent({
  name: 'OddsEventComponent',
  props: {
    event: {
      type: Object as PropType<PreviewEvent>,
      required: true
    },
    isFullScreen: {
      type: Boolean,
      required: false,
      default: false
    },
    sportId: {
      type: String,
      required: true
    },
    categoryId: {
      type: String,
      required: true
    },
    tournamentId: {
      type: String,
      required: true
    },
    baseUrl: {
      type: String,
      required: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    columns: {
      type: Array as PropType<string[]>,
      required: false,
      default: () => []
    }
  },

  components: {
    OddsInfo,
    OddsOutcomeColumn
  },
  computed: {
    isOddsLoading(): boolean {
      if (!this.event || !this.event.odds || this.event.odds.length === 0) {
        return true;
      }

      // Se ci sono colonne definite, non considerare come loading anche se gli outcomes sono vuoti
      // (potrebbero essere placeholder che verranno riempiti)
      if (this.columns && this.columns.length > 0) {
        return false;
      }

      // Controlla se almeno un odd ha outcomes non vuoti
      const hasOutcomes = this.event.odds.some(odd => {
        return Array.isArray(odd.outcome) && odd.outcome.length > 0;
      });

      return !hasOutcomes;
    },
    outcomesColumns(): PreviewOutcome[][] {
      if (this.loading) {
        return Array(3)
          .fill(0)
          .map(() => [{} as any]);
      }

      if (!this.event || !this.event.odds || this.event.odds.length === 0) {
        return [];
      }

      // Helper per creare una colonna filler vuota
      const createFillerColumn = (maxLength: number): PreviewOutcome[] => {
        return Array(maxLength)
          .fill(null)
          .map(
            () =>
              ({
                unique_id: '',
                outcome_id: 0,
                outcome_odd_value: '',
                status: 0, // Status 0 fa mostrare il lock
                name: '',
                outcome_alias: '',
                outcome_name: '',
                id_outcome: 0,
                ext_id: '',
                lastUpdate: '',
                sort: 0,
                alias: '',
                odd_percent: '',
                outcome_odd_value_for_order: '',
                active: false,
                specifier: ''
              } as PreviewOutcome)
          );
      };

      // Se odds.length > 1: crea una colonna per ogni odd con tutti gli outcomes di quell'odd
      // Nota: gli outcomes sono già filtrati per mainLine con fallback in OddsEventsGroup.vue
      if (this.event.odds.length > 1) {
        const columnsMap = new Map<string, PreviewOutcome[]>();
        this.event.odds.forEach(odd => {
          const marketId = odd.unique_id || odd.id;
          const marketName = odd.marketName || '';

          if (marketName) {
            // odd.outcome è sempre un array semplice di PreviewOutcome[] già filtrato
            if (
              odd.outcome &&
              Array.isArray(odd.outcome) &&
              odd.outcome.length > 0
            ) {
              // Aggiungi il marketId solo se non è già presente nell'outcome
              const outcomesWithMarketId = (
                odd.outcome as PreviewOutcome[]
              ).map(
                outcome =>
                  ({
                    ...outcome,
                    marketId: (outcome as any).marketId || marketId
                  } as PreviewOutcome & { marketId: string | number })
              );
              columnsMap.set(marketName, outcomesWithMarketId);
            } else {
              // Odd placeholder con outcome vuoto: aggiungi comunque alla mappa con array vuoto
              // Verrà gestito successivamente quando si creano le colonne filler
              columnsMap.set(marketName, []);
            }
          }
        });

        // Trova il numero massimo di outcomes tra tutte le colonne
        // Se tutte le colonne sono vuote e ci sono colonne definite, usa almeno 1
        const maxLengthFromColumns =
          columnsMap.size > 0
            ? Math.max(
                ...Array.from(columnsMap.values()).map(col => col.length)
              )
            : 0;
        const maxLength =
          maxLengthFromColumns > 0
            ? maxLengthFromColumns
            : this.columns && this.columns.length > 0
            ? 1
            : 0;

        // Riempi le colonne più corte con placeholder che mostrano il lock
        const paddedColumnsMap = new Map<string, PreviewOutcome[]>();
        columnsMap.forEach((column, marketName) => {
          if (column.length === 0 && maxLength > 0) {
            // Colonna vuota: crea placeholder
            paddedColumnsMap.set(marketName, createFillerColumn(maxLength));
          } else {
            const paddingNeeded = maxLength - column.length;
            const padding: PreviewOutcome[] = Array(paddingNeeded)
              .fill(null)
              .map(
                () =>
                  ({
                    unique_id: '',
                    outcome_id: 0,
                    outcome_odd_value: '',
                    status: 0, // Status 0 fa mostrare il lock
                    name: '',
                    outcome_alias: '',
                    outcome_name: '',
                    id_outcome: 0,
                    ext_id: '',
                    lastUpdate: '',
                    sort: 0,
                    alias: '',
                    odd_percent: '',
                    outcome_odd_value_for_order: '',
                    active: false,
                    specifier: ''
                  } as PreviewOutcome)
              );
            paddedColumnsMap.set(marketName, [...column, ...padding]);
          }
        });

        // Se columns è fornito, aggiungi colonne filler per quelle mancanti
        if (this.columns && this.columns.length > 0) {
          const resultColumns: PreviewOutcome[][] = [];
          this.columns.forEach(columnName => {
            if (paddedColumnsMap.has(columnName)) {
              resultColumns.push(paddedColumnsMap.get(columnName)!);
            } else {
              // Colonna mancante: aggiungi colonna filler con almeno un elemento
              resultColumns.push(createFillerColumn(Math.max(maxLength, 1)));
            }
          });
          return resultColumns;
        }

        // Fallback: restituisci le colonne nell'ordine originale se columns non è fornito
        return Array.from(paddedColumnsMap.values());
      }

      // Se odds.length === 1: crea una colonna per ogni outcome dell'unico odd
      // Nota: gli outcomes sono già filtrati per mainLine con fallback in OddsEventsGroup.vue
      const firstOdd = this.event.odds[0];
      if (!firstOdd || !firstOdd.outcome || !Array.isArray(firstOdd.outcome)) {
        return [];
      }

      // Gli outcomes sono già filtrati, usa direttamente quelli dell'evento
      const outcomes = firstOdd.outcome as PreviewOutcome[];

      // Assicurati che ogni outcome abbia il marketId
      // Usa il marketId esistente o calcola da odd.unique_id || odd.id
      const marketId = firstOdd.unique_id || firstOdd.id;
      const outcomesWithMarketId = outcomes.map(
        outcome =>
          ({
            ...outcome,
            marketId: (outcome as any).marketId || marketId
          } as PreviewOutcome & { marketId: string | number })
      );

      // Crea una mappa delle colonne esistenti per nome
      const columnsMap = new Map<string, PreviewOutcome[]>();
      outcomesWithMarketId.forEach(outcome => {
        const columnName = outcome.outcome_name || outcome.outcome_alias || '';
        if (columnName) {
          columnsMap.set(columnName, [outcome]);
        }
      });

      // Se columns è fornito, aggiungi colonne filler per quelle mancanti
      if (this.columns && this.columns.length > 0) {
        const resultColumns: PreviewOutcome[][] = [];
        this.columns.forEach(columnName => {
          if (columnsMap.has(columnName)) {
            resultColumns.push(columnsMap.get(columnName)!);
          } else {
            // Colonna mancante: aggiungi colonna filler con un solo elemento
            resultColumns.push([
              {
                unique_id: '',
                outcome_id: 0,
                outcome_odd_value: '',
                status: 0, // Status 0 fa mostrare il lock
                name: '',
                outcome_alias: '',
                outcome_name: '',
                id_outcome: 0,
                ext_id: '',
                lastUpdate: '',
                sort: 0,
                alias: '',
                odd_percent: '',
                outcome_odd_value_for_order: '',
                active: false,
                specifier: ''
              } as PreviewOutcome
            ]);
          }
        });
        return resultColumns;
      }

      // Fallback: trasformare da array semplice a array di array (ogni outcome diventa una colonna)
      return outcomesWithMarketId.map(outcome => [outcome]);
    }
  },
  methods: {
    addOddToCart(event: PreviewEvent, $event: any) {
      // Usa il marketId dall'outcome se presente (aggiunto durante la trasformazione)
      const firstOdd = event.odds[0];
      const marketId = $event?.marketId || firstOdd?.unique_id || firstOdd?.id;
      this.$emit('add-odd-to-cart', {
        marketId: marketId,
        ...$event
      });
    }
  }
});
